import React, { memo, useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import { Advice, Button, Icon, Modal, Popover, Text, useTheme, Spacer } from '@Sage/hoi-poi-ui';

import AbsoluteError from 'components/AbsoluteError';
import HorizontalScroll from 'components/HorizontalScroll';
import {
    WidgetContentLayout,
    WidgetEmpty,
    WidgetHeaderLayout,
    WidgetLayout,
} from 'containers/components/widgets/Layouts';
import { EntityDetailActions, EntityCrudActions } from 'actions';
import {
    getSalesOrderStatusInfo,
    getSalesOrdersStatusChangeLog,
    setSalesOrderSyncSage,
} from 'services/SalesService';
import { getLiteral } from 'utils/getLiteral';
import { getSrcUserCircleAvatar } from 'utils/getSrcAvatar';
import { logEvent } from 'utils/tracking';
import { publish } from 'lib/EventBuser';
import { UPDATE_ROW } from 'lib/events';

import StatusChangeLogModal from '../components/StatusChangeLogModal';
import StatusItem from './StatusItem';
import './styles.scss';

const initialState = {
    actionLoading: false,
    error: null,
    isActionModalOpen: false,
    loading: false,
    pipeStatuses: [],
};

const reducer = (state, action) => {
    switch (action.type) {
        case 'SET_ERROR':
            return { ...state, error: action.payload };
        case 'SET_LOADING':
            return { ...state, loading: action.payload };
        case 'SET_ACTION_LOADING':
            return { ...state, actionLoading: action.payload };
        case 'SET_ACTION_MODAL_OPEN':
            return { ...state, isActionModalOpen: action.payload };
        case 'SET_PIPE_STATUSES':
            return { ...state, pipeStatuses: action.payload };
        case 'SET_STATUS_DATA':
            return {
                ...state,
                error: null,
                pipeStatuses: action.payload.pipeStatuses,
            };
        case 'RESET':
            return initialState;
        default:
            return state;
    }
};

const SageStatusPipelineWidget = ({ data, entity, setSize }) => {
    const dispatch = useDispatch();
    const theme = useTheme();
    const onRefModal = useRef();
    const scrollbarRef = useRef();
    const changeLog = useRef();
    const pollingInterval = useRef();
    const previousStatusId = useRef();

    const [state, dispatchState] = useReducer(reducer, initialState);

    const processChangeLog = useCallback((changeLogData) => {
        return (
            changeLogData?.map((log) => {
                const userImage = getSrcUserCircleAvatar(log.user);
                return {
                    ...log,
                    date: moment
                        .utc(log.date, 'YYYY-MM-DDTHH:mm:ss')
                        .local()
                        .format('YYYY-MM-DDTHH:mm:ss'),
                    placeholderUser: userImage.fallbackSrc,
                    srcUser: userImage.src,
                };
            }) || []
        );
    }, []);

    const fetchStatusData = useCallback(
        (showLoading = true) => {
            if (!data?.id) {
                return Promise.resolve();
            }

            if (showLoading) {
                dispatchState({ type: 'SET_LOADING', payload: true });
            }
            return Promise.all([
                getSalesOrderStatusInfo({ idSalesOrder: data.id }),
                getSalesOrdersStatusChangeLog(data.id),
            ])
                .then(([statusInfo, changeLogData]) => {
                    if (!statusInfo?.length) {
                        return;
                    }

                    changeLog.current = processChangeLog(changeLogData?.data);

                    const newPipeStatuses = statusInfo.map((status) => {
                        return {
                            actionButtonText: status.actionButtonText,
                            description: getLiteral(status.description),
                            errorDescription: status.errorDescription,
                            extId: status.extId,
                            hasAction: status.hasAction,
                            hasActionEnabled: status.hasActionEnabled,
                            id: status.id,
                            isSelected: status.isCurrent,
                            label: status.name,
                            modalText: status.modalText,
                            modalTitle: status.modalTitle,
                            order: status.order,
                            statusType: status.statusType,
                            tooltipDescription: getLiteral(status.tooltipDescription),
                            value: status.id,
                        };
                    });

                    const currentStatus = newPipeStatuses.find((status) => status.isSelected);
                    const hasStatusChanged =
                        previousStatusId.current !== null &&
                        previousStatusId.current !== undefined &&
                        previousStatusId.current !== currentStatus?.id;

                    dispatchState({
                        type: 'SET_STATUS_DATA',
                        payload: { pipeStatuses: newPipeStatuses },
                    });

                    // Update local data if the status changed
                    if (hasStatusChanged && currentStatus && data?.id) {
                        const fieldsToUpdate = {};

                        if (currentStatus.id) {
                            fieldsToUpdate.idStateSalesOrder = currentStatus.id;
                            if (currentStatus.label) {
                                fieldsToUpdate.stateSalesOrder = currentStatus.label;
                            }

                            dispatch(
                                EntityDetailActions.updateDetailFields(
                                    entity,
                                    data.id,
                                    fieldsToUpdate,
                                ),
                            );

                            dispatch(EntityCrudActions.changeFields(fieldsToUpdate));
                            publish(`${UPDATE_ROW}_${entity.entity}`, {
                                id: data.id,
                                fields: fieldsToUpdate,
                            });
                        }
                    }

                    if (currentStatus) {
                        previousStatusId.current = currentStatus.id;
                    }

                    return newPipeStatuses;
                })
                .catch((e) => {
                    console.error(e);
                    dispatchState({ type: 'SET_ERROR', payload: e });
                    throw e;
                })
                .finally(() => {
                    if (showLoading) {
                        dispatchState({ type: 'SET_LOADING', payload: false });
                    }
                });
        },
        [data, dispatch, entity, processChangeLog],
    );

    const startPolling = useCallback(() => {
        if (pollingInterval.current) {
            clearInterval(pollingInterval.current);
        }

        // every 5 minutes
        pollingInterval.current = setInterval(() => {
            fetchStatusData(false);
        }, 300000);
    }, [fetchStatusData]);

    useEffect(() => {
        fetchStatusData().then((statuses) => {
            if (statuses) {
                const currentStatus = statuses.find((status) => status.isSelected);
                if (currentStatus) {
                    previousStatusId.current = currentStatus.id;
                }
                if (currentStatus?.extId === 'syncPending') {
                    startPolling();
                }
            }
        });

        return () => {
            if (pollingInterval.current) {
                clearInterval(pollingInterval.current);
                pollingInterval.current = null;
            }
        };
    }, [fetchStatusData, startPolling]);

    const seeChangeLog = useCallback(() => {
        onRefModal.current?.open({ changeLog: changeLog.current });
        logEvent({
            event: entity.trueName,
            functionality: 'seeChangeLog',
        });
    }, [entity?.trueName]);

    const handleOnRef = useCallback((ref) => (onRefModal.current = ref), []);

    const scrollToItem = useCallback((index) => {
        scrollbarRef.current?.scrollLeft(index * 85);
    }, []);

    const handleStatusAction = useCallback(() => {
        dispatchState({ type: 'SET_ACTION_MODAL_OPEN', payload: true });
    }, []);

    const executeStatusAction = useCallback(() => {
        if (!data?.id) return;

        dispatchState({ type: 'SET_ACTION_MODAL_OPEN', payload: false });
        dispatchState({ type: 'SET_ACTION_LOADING', payload: true });
        setSalesOrderSyncSage({ idSalesOrder: data.id })
            .then(() => fetchStatusData())
            .then(() => {
                startPolling();
            })
            .catch((e) => {
                console.error(e);
                dispatchState({ type: 'SET_ERROR', payload: e });
            })
            .finally(() => {
                dispatchState({ type: 'SET_ACTION_LOADING', payload: false });
            });
    }, [data?.id, fetchStatusData, startPolling]);

    const closeActionModal = useCallback(() => {
        dispatchState({ type: 'SET_ACTION_MODAL_OPEN', payload: false });
    }, []);

    const currentStatus = useMemo(() => {
        return state.pipeStatuses.find((status) => status.isSelected);
    }, [state.pipeStatuses]);

    const isCurrentStatusError = useMemo(() => {
        return currentStatus?.statusType?.toLowerCase() === 'error';
    }, [currentStatus]);

    const showPipe = !state.error && !!state.pipeStatuses.length;
    const isEmpty = !state.error && !state.pipeStatuses.length;

    const titleWithInfo = (
        <div className="sage-status-pipeline-widget__title">
            <Text>{getLiteral('label_quote_sync_status_status_review')}</Text>
            <Popover
                content={
                    <div className="sage-status-pipeline-widget__info-popover">
                        <Text type="subtitle" medium>
                            {getLiteral('label_quote_sync_status_status_review_title')}
                        </Text>
                        <Spacer y={2} />
                        <Text>{getLiteral('label_quote_sync_status_status_review_desc')}</Text>
                    </div>
                }
                position="bottom"
                trigger="hover"
            >
                <Icon name="info" size="small" />
            </Popover>
        </div>
    );

    return (
        <WidgetLayout
            data={data}
            loading={state.loading}
            setSize={setSize}
            type="pipeline"
            className="sage-status-pipeline-widget"
        >
            <WidgetHeaderLayout
                content={[{ title: titleWithInfo }]}
                rightPostComponent={
                    <Button
                        type="primary-soft"
                        size="small"
                        icon="changeLog"
                        iconPosition="right"
                        onClick={seeChangeLog}
                    >
                        {getLiteral('label_see_change_log')}
                    </Button>
                }
                rightPostComponentWithoutDivider
            />
            <WidgetContentLayout>
                {isEmpty && (
                    <WidgetEmpty
                        icon={<Icon name="thumbs" size="big" />}
                        text={getLiteral('label_without_statuses_widget')}
                    />
                )}
                {state.error && <AbsoluteError size="small" />}
                {showPipe && !state.loading && (
                    <>
                        <HorizontalScroll
                            className="sage-status-pipeline-widget__statuses"
                            width={100 * (state.pipeStatuses?.length || 1)}
                            scrollbarRef={(ref) => (scrollbarRef.current = ref)}
                        >
                            {state.pipeStatuses.map((item, idx) => (
                                <StatusItem
                                    key={item.value}
                                    {...item}
                                    index={idx}
                                    nextStatusType={state.pipeStatuses[idx + 1]?.statusType}
                                    prevStatusType={state.pipeStatuses[idx - 1]?.statusType}
                                    scrollHere={scrollToItem}
                                />
                            ))}
                        </HorizontalScroll>
                        {isCurrentStatusError ? (
                            <Advice type="error" showIcon showCollapse={false}>
                                {currentStatus?.errorDescription
                                    ? getLiteral(currentStatus.errorDescription)
                                    : getLiteral('label_quote_sync_status_sync_error')}
                            </Advice>
                        ) : (
                            currentStatus?.description && (
                                <div className="sage-status-pipeline-widget__status-details">
                                    <Text
                                        color={theme.colors.textLight.secondary}
                                        className="sage-status-pipeline-widget__status-details-label"
                                    >
                                        {getLiteral('label_quote_sync_status_status_details')}
                                    </Text>
                                    <div className="sage-status-pipeline-widget__status-details-content">
                                        <Text
                                            type="body"
                                            className="sage-status-pipeline-widget__status-details-description"
                                        >
                                            {currentStatus.description}
                                        </Text>
                                    </div>
                                </div>
                            )
                        )}
                        {currentStatus?.hasAction && (
                            <div className="sage-status-pipeline-widget__status-action">
                                <Button
                                    isLoading={state.actionLoading}
                                    onClick={handleStatusAction}
                                    type="primary"
                                >
                                    {getLiteral(currentStatus.actionButtonText)}
                                </Button>
                            </div>
                        )}
                    </>
                )}
                <StatusChangeLogModal onRef={handleOnRef} />
                {currentStatus?.hasAction && (
                    <Modal
                        isOpen={state.isActionModalOpen}
                        onRequestClose={closeActionModal}
                        onCancel={closeActionModal}
                        onConfirm={executeStatusAction}
                        title={
                            currentStatus?.modalTitle ? getLiteral(currentStatus.modalTitle) : ''
                        }
                        confirmText={getLiteral('action_got_it')}
                        cancelText={getLiteral('action_cancel')}
                    >
                        <Text>
                            {currentStatus?.modalText ? getLiteral(currentStatus.modalText) : ''}
                        </Text>
                    </Modal>
                )}
            </WidgetContentLayout>
        </WidgetLayout>
    );
};

export default memo(SageStatusPipelineWidget);
